import { useState, useEffect, useRef } from 'react';
import CryptoJS from 'crypto-js';
import { 
  Search, 
  Filter, 
  Phone, 
  Mail, 
  Building2, 
  User, 
  MessageSquare,
  Calendar,
  CheckCircle2,
  Clock,
  XCircle,
  FileDown,
  Briefcase,
  Package,
  BarChart3,
  Users,
  Eye,
  Monitor,
  Smartphone,
  Globe,
  Lock,
  LogOut,
  EyeOff,
  Network,
  Settings,
} from 'lucide-react';
import { getVisitorStats } from '../utils/visitorTracking';
import ContentManager from './ContentManager';
import { formApi } from '../services/api';

const DEFAULT_USERNAME = 'admin';
const DEFAULT_PASSWORD_HASH = '5f4dcc3b5aa765d61d8327deb882cf99';

interface ConsultationForm {
  id: number;
  name: string;
  company: string;
  phone: string;
  email: string | null;
  industry: string | null;
  product: string | null;
  message: string | null;
  status: 'pending' | 'processed' | 'closed';
  notes: string | null;
  ip_address: string | null;
  created_at: string;
  updated_at: string;
}

interface VisitorStats {
  uniqueVisitors: number;
  totalVisits: number;
  avgDuration: number;
  deviceStats: Record<string, number>;
  browserStats: Record<string, number>;
  pageStats: Record<string, number>;
}

function md5(input: string): string {
  return CryptoJS.MD5(input).toString();
}

function SettingsPanel() {
  const [currentUsername, setCurrentUsername] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeSection, setActiveSection] = useState<'username' | 'password'>('password');

  const savedUsername = localStorage.getItem('admin_username') || DEFAULT_USERNAME;
  const savedHash = localStorage.getItem('admin_password_hash') || DEFAULT_PASSWORD_HASH;

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('请填写所有密码字段');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('新密码与确认密码不一致');
      return;
    }

    if (newPassword.length < 6) {
      setError('新密码长度至少6位');
      return;
    }

    const currentHash = md5(currentPassword);
    if (currentHash !== savedHash) {
      setError('当前密码错误');
      return;
    }

    const newHash = md5(newPassword);
    localStorage.setItem('admin_password_hash', newHash);
    
    setSuccess('密码修改成功！下次登录请使用新密码');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleChangeUsername = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentUsername || !newUsername) {
      setError('请填写所有字段');
      return;
    }

    if (currentUsername !== savedUsername) {
      setError('当前用户名错误');
      return;
    }

    if (newUsername.length < 3) {
      setError('新用户名至少3位');
      return;
    }

    localStorage.setItem('admin_username', newUsername);
    
    setSuccess('用户名修改成功！下次登录请使用新用户名');
    setCurrentUsername('');
    setNewUsername('');
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveSection('password')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeSection === 'password'
                ? 'bg-ocean-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            修改密码
          </button>
          <button
            onClick={() => setActiveSection('username')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeSection === 'username'
                ? 'bg-ocean-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            修改用户名
          </button>
        </div>

        {activeSection === 'password' ? (
          <>
            <h2 className="text-xl font-bold text-slate-900 mb-2">修改密码</h2>
            <p className="text-slate-500 text-sm mb-6">定期更换密码可以提高账户安全性</p>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">当前密码</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none pr-12"
                    placeholder="请输入当前密码"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">新密码</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none pr-12"
                    placeholder="请输入新密码（至少6位）"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">确认新密码</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none pr-12"
                    placeholder="请再次输入新密码"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {success && (
                <div className="text-emerald-600 text-sm bg-emerald-50 p-3 rounded-lg flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  {success}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition-colors font-medium"
              >
                修改密码
              </button>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold text-slate-900 mb-2">修改用户名</h2>
            <p className="text-slate-500 text-sm mb-6">当前用户名: <span className="font-medium text-slate-700">{savedUsername}</span></p>

            <form onSubmit={handleChangeUsername} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">当前用户名</label>
                <input
                  type="text"
                  value={currentUsername}
                  onChange={(e) => setCurrentUsername(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none"
                  placeholder="请输入当前用户名"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">新用户名</label>
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none"
                  placeholder="请输入新用户名（至少3位）"
                />
              </div>

              {error && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {success && (
                <div className="text-emerald-600 text-sm bg-emerald-50 p-3 rounded-lg flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  {success}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition-colors font-medium"
              >
                修改用户名
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [forms, setForms] = useState<ConsultationForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'processed' | 'closed'>('all');
  const [selectedForm, setSelectedForm] = useState<ConsultationForm | null>(null);
  const [notes, setNotes] = useState('');
  const [activeTab, setActiveTab] = useState<'forms' | 'analytics' | 'content' | 'settings'>('forms');
  const [visitorStats, setVisitorStats] = useState<VisitorStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const isInitialLoadRef = useRef(true);
  const isLoadingRef = useRef(false);

  // 初始化认证状态
  useEffect(() => {
    const checkAuth = () => {
      const auth = localStorage.getItem('admin_auth');
      const newAuthState = auth === 'true';
      setIsAuthenticated(newAuthState);
      setAuthChecked(true);
    };
    
    checkAuth();
  }, []);

  // 加载数据 - 认证后立即加载一次
  useEffect(() => {
    if (isAuthenticated && isInitialLoadRef.current) {
      fetchForms();
      loadVisitorStats();
    }
  }, [isAuthenticated]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    const hash = md5(password);
    const savedUsername = localStorage.getItem('admin_username') || DEFAULT_USERNAME;
    const savedHash = localStorage.getItem('admin_password_hash') || DEFAULT_PASSWORD_HASH;
    
    if (username === savedUsername && hash === savedHash) {
      setIsAuthenticated(true);
      localStorage.setItem('admin_auth', 'true');
      setLoginError('');
    } else {
      setLoginError('用户名或密码错误');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('admin_auth');
    setUsername('');
    setPassword('');
    setForms([]);
    setAuthChecked(false);
    isInitialLoadRef.current = true;
  };

  const loadVisitorStats = async () => {
    try {
      const stats = await getVisitorStats();
      if (stats) {
        setVisitorStats(stats);
      }
    } catch (err) {
      console.error('[Stats] 加载访客统计失败:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchForms = async () => {
    if (!isAuthenticated) {
      return;
    }
    
    if (isLoadingRef.current) {
      return;
    }
    
    try {
      isLoadingRef.current = true;
      setIsSyncing(true);
      if (isInitialLoadRef.current) {
        setLoading(true);
      }
      
      const response = await formApi.getAll();
      
      if (response.success && response.data) {
        setForms(response.data);
      } else {
        console.error('[Fetch] 获取数据失败:', response.message);
      }
      
      setLastSyncTime(new Date());
      isInitialLoadRef.current = false;
    } catch (err: any) {
      console.error('[Fetch] 获取数据失败:', err);
      if (err.message?.includes('Permission denied')) {
        alert('您没有权限查看此数据');
      }
    } finally {
      setLoading(false);
      setIsSyncing(false);
      isLoadingRef.current = false;
    }
  };

  const updateStatus = async (id: number, newStatus: 'pending' | 'processed' | 'closed') => {
    try {
      const response = await formApi.updateStatus(id, newStatus);
      
      if (response.success) {
        await fetchForms();
      } else {
        if (response.code === 'PERMISSION_DENIED') {
          alert('您没有权限执行此操作');
        }
      }
    } catch (err: any) {
      console.error('[Update] 更新状态失败:', err);
    }
  };

  const updateNotes = async (id: number) => {
    try {
      const response = await formApi.updateNotes(id, notes);
      
      if (response.success) {
        setSelectedForm(null);
        setNotes('');
        await fetchForms();
      } else {
        if (response.code === 'PERMISSION_DENIED') {
          alert('您没有权限执行此操作');
        }
      }
    } catch (err: any) {
      console.error('[Update] 更新备注失败:', err);
    }
  };

  const exportToCSV = () => {
    const headers = ['ID', '姓名', '公司', '电话', '邮箱', '所属行业', '关注产品', '补充说明', 'IP地址', '状态', '备注', '提交时间'];
    const rows = filteredForms.map(form => [
      form.id,
      form.name,
      form.company,
      form.phone,
      form.email || '',
      form.industry || '',
      form.product || '',
      form.message || '',
      form.ip_address || '',
      getStatusText(form.status),
      form.notes || '',
      new Date(form.created_at).toLocaleString('zh-CN')
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `客户咨询表单_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '待处理';
      case 'processed': return '已处理';
      case 'closed': return '已关闭';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'processed': return 'bg-emerald-100 text-emerald-700';
      case 'closed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'processed': return <CheckCircle2 className="w-4 h-4" />;
      case 'closed': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const getDeviceIcon = (device: string) => {
    switch (device) {
      case 'desktop': return <Monitor className="w-4 h-4" />;
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Smartphone className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  const getDeviceLabel = (device: string) => {
    switch (device) {
      case 'desktop': return '桌面端';
      case 'mobile': return '移动端';
      case 'tablet': return '平板';
      default: return device;
    }
  };

  const filteredForms = forms.filter(form => {
    const matchesSearch = 
      form.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.phone.includes(searchTerm) ||
      (form.industry && form.industry.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (form.product && form.product.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (form.ip_address && form.ip_address.includes(searchTerm));
    const matchesStatus = statusFilter === 'all' || form.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: forms.length,
    pending: forms.filter(f => f.status === 'pending').length,
    processed: forms.filter(f => f.status === 'processed').length,
    closed: forms.filter(f => f.status === 'closed').length,
  };

  if (!authChecked) {
    return <div className="min-h-screen bg-slate-50" />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {!isAuthenticated && (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-ocean-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-ocean-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">管理后台登录</h1>
              <p className="text-slate-500 mt-2">请输入用户名和密码</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">用户名</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none"
                    placeholder="请输入用户名"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">密码</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none"
                    placeholder="请输入密码"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {loginError && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{loginError}</div>
              )}
              
              <button
                type="submit"
                className="w-full py-3 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition-colors font-medium"
              >
                登录
              </button>
            </form>
          </div>
        </div>
      )}

      {isAuthenticated && (
        <>
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">管理后台</h1>
                  <p className="text-sm text-slate-500">客户咨询与访问数据分析</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex bg-slate-100 rounded-lg p-1">
                    <button
                      onClick={() => setActiveTab('forms')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === 'forms' 
                          ? 'bg-white text-slate-900 shadow-sm' 
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      咨询表单
                    </button>
                    <button
                      onClick={() => setActiveTab('analytics')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === 'analytics' 
                          ? 'bg-white text-slate-900 shadow-sm' 
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      访问统计
                    </button>
                    <button
                      onClick={() => setActiveTab('content')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === 'content' 
                          ? 'bg-white text-slate-900 shadow-sm' 
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      内容发布
                    </button>
                    <button
                      onClick={() => setActiveTab('settings')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                        activeTab === 'settings' 
                          ? 'bg-white text-slate-900 shadow-sm' 
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <Settings className="w-4 h-4" />
                      设置
                    </button>
                  </div>
                  {activeTab === 'forms' && (
                    <>
                      <button
                        onClick={fetchForms}
                        disabled={isSyncing}
                        className="flex items-center gap-2 px-4 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition-colors disabled:opacity-50"
                      >
                        <div className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </div>
                        {isSyncing ? '同步中...' : '刷新'}
                      </button>
                      <button
                        onClick={exportToCSV}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                      >
                        <FileDown className="w-4 h-4" />
                        导出CSV
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    退出
                  </button>
                </div>
              </div>
            </div>
          </header>

          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {activeTab === 'content' ? (
              <ContentManager />
            ) : activeTab === 'settings' ? (
              <SettingsPanel />
            ) : activeTab === 'forms' ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
                    <div className="text-sm text-slate-500">总咨询数</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
                    <div className="text-sm text-slate-500">待处理</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="text-2xl font-bold text-emerald-600">{stats.processed}</div>
                    <div className="text-sm text-slate-500">已处理</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="text-2xl font-bold text-slate-600">{stats.closed}</div>
                    <div className="text-sm text-slate-500">已关闭</div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        placeholder="搜索姓名、公司、电话、行业、产品或IP..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Filter className="w-5 h-5 text-slate-400" />
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as any)}
                        className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none"
                      >
                        <option value="all">全部状态</option>
                        <option value="pending">待处理</option>
                        <option value="processed">已处理</option>
                        <option value="closed">已关闭</option>
                      </select>
                    </div>
                  </div>
                  {lastSyncTime && (
                    <div className="mt-3 text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      上次同步: {lastSyncTime.toLocaleString('zh-CN')}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">客户信息</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">行业/产品</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">联系方式</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">IP地址</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">状态</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">提交时间</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">操作</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {loading ? (
                          <tr>
                            <td colSpan={7} className="px-6 py-12 text-center">
                              <div className="w-8 h-8 border-2 border-ocean-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                              <p className="text-slate-500">加载中...</p>
                            </td>
                          </tr>
                        ) : filteredForms.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-6 py-12 text-center">
                              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Search className="w-8 h-8 text-slate-400" />
                              </div>
                              <p className="text-slate-500">暂无数据</p>
                            </td>
                          </tr>
                        ) : (
                          filteredForms.map((form) => (
                          <tr key={form.id} className="hover:bg-slate-50">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-ocean-100 rounded-full flex items-center justify-center">
                                  <User className="w-5 h-5 text-ocean-600" />
                                </div>
                                <div>
                                  <div className="font-medium text-slate-900">{form.name}</div>
                                  <div className="text-sm text-slate-500 flex items-center gap-1">
                                    <Building2 className="w-3 h-3" />
                                    {form.company}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-1">
                                {form.industry && (
                                  <div className="flex items-center gap-1 text-sm text-slate-600">
                                    <Briefcase className="w-3 h-3 text-ocean-500" />
                                    {form.industry}
                                  </div>
                                )}
                                {form.product && (
                                  <div className="flex items-center gap-1 text-sm text-slate-600">
                                    <Package className="w-3 h-3 text-emerald-500" />
                                    {form.product}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-1">
                                <a href={`tel:${form.phone}`} className="flex items-center gap-2 text-sm text-ocean-600 hover:text-ocean-700">
                                  <Phone className="w-4 h-4" />
                                  {form.phone}
                                </a>
                                {form.email && (
                                  <a href={`mailto:${form.email}`} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700">
                                    <Mail className="w-4 h-4" />
                                    {form.email}
                                  </a>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Network className="w-4 h-4 text-slate-400" />
                                <span className="font-mono">{form.ip_address || '-'}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(form.status)}`}>
                                {getStatusIcon(form.status)}
                                {getStatusText(form.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Calendar className="w-4 h-4" />
                                {new Date(form.created_at).toLocaleString('zh-CN')}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {form.status === 'pending' && (
                                  <button
                                    onClick={() => updateStatus(form.id, 'processed')}
                                    className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-sm hover:bg-emerald-200 transition-colors"
                                  >
                                    标记已处理
                                  </button>
                                )}
                                {form.status !== 'closed' && (
                                  <button
                                    onClick={() => updateStatus(form.id, 'closed')}
                                    className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors"
                                  >
                                    关闭
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setSelectedForm(form);
                                    setNotes(form.notes || '');
                                  }}
                                  className="px-3 py-1 bg-ocean-100 text-ocean-700 rounded-lg text-sm hover:bg-ocean-200 transition-colors"
                                >
                                  详情
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-8">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-ocean-100 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-ocean-600" />
                      </div>
                      <div className="text-sm text-slate-500">独立访客</div>
                    </div>
                    <div className="text-3xl font-bold text-slate-900">
                      {statsLoading ? '-' : visitorStats?.uniqueVisitors || 0}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">近30天</div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Eye className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="text-sm text-slate-500">总访问量</div>
                    </div>
                    <div className="text-3xl font-bold text-slate-900">
                      {statsLoading ? '-' : visitorStats?.totalVisits || 0}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">近30天</div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="text-sm text-slate-500">平均停留</div>
                    </div>
                    <div className="text-3xl font-bold text-slate-900">
                      {statsLoading ? '-' : `${visitorStats?.avgDuration || 0}s`}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">近30天</div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="text-sm text-slate-500">平均PV/UV</div>
                    </div>
                    <div className="text-3xl font-bold text-slate-900">
                      {statsLoading ? '-' : visitorStats?.uniqueVisitors 
                        ? (visitorStats.totalVisits / visitorStats.uniqueVisitors).toFixed(1) 
                        : '0'}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">人均浏览</div>
                  </div>
                </div>

                {visitorStats?.deviceStats && Object.keys(visitorStats.deviceStats).length > 0 && (
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Monitor className="w-5 h-5 text-ocean-500" />
                      设备分布
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(visitorStats.deviceStats).map(([device, count]) => {
                        const percentage = visitorStats.totalVisits > 0 
                          ? Math.round((count / visitorStats.totalVisits) * 100) 
                          : 0;
                        return (
                          <div key={device} className="text-center">
                            <div className="flex items-center justify-center gap-2 mb-2">
                              {getDeviceIcon(device)}
                              <span className="text-sm font-medium text-slate-700">{getDeviceLabel(device)}</span>
                            </div>
                            <div className="text-2xl font-bold text-slate-900">{count}</div>
                            <div className="text-xs text-slate-500">{percentage}%</div>
                            <div className="w-full bg-slate-100 rounded-full h-2 mt-2">
                              <div 
                                className="bg-ocean-500 h-2 rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {visitorStats?.browserStats && Object.keys(visitorStats.browserStats).length > 0 && (
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Globe className="w-5 h-5 text-emerald-500" />
                      浏览器分布
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(visitorStats.browserStats).map(([browser, count]) => {
                        const percentage = visitorStats.totalVisits > 0 
                          ? Math.round((count / visitorStats.totalVisits) * 100) 
                          : 0;
                        return (
                          <div key={browser} className="bg-slate-50 rounded-lg p-4">
                            <div className="text-sm font-medium text-slate-700 mb-1">{browser}</div>
                            <div className="flex items-baseline gap-2">
                              <span className="text-xl font-bold text-slate-900">{count}</span>
                              <span className="text-xs text-slate-500">{percentage}%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {visitorStats?.pageStats && Object.keys(visitorStats.pageStats).length > 0 && (
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-purple-500" />
                      页面访问排行
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(visitorStats.pageStats)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 10)
                        .map(([page, count], index) => {
                          const percentage = visitorStats.totalVisits > 0 
                            ? Math.round((count / visitorStats.totalVisits) * 100) 
                            : 0;
                          return (
                            <div key={page} className="flex items-center gap-4">
                              <div className="w-6 text-center text-sm font-medium text-slate-400">{index + 1}</div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm text-slate-700">{page === '/' ? '首页' : page}</span>
                                  <span className="text-sm font-medium text-slate-900">{count}</span>
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-2">
                                  <div 
                                    className="bg-purple-500 h-2 rounded-full transition-all"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                              <div className="w-12 text-right text-xs text-slate-500">{percentage}%</div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {(!visitorStats || visitorStats.totalVisits === 0) && !statsLoading && (
                  <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BarChart3 className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-500">暂无访问数据</p>
                    <p className="text-sm text-slate-400 mt-1">数据将在用户访问网站后自动记录</p>
                  </div>
                )}
              </div>
            )}
          </main>

          {selectedForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-slate-900">咨询详情</h2>
                    <button
                      onClick={() => setSelectedForm(null)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <XCircle className="w-5 h-5 text-slate-500" />
                    </button>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-500">姓名</label>
                      <div className="font-medium text-slate-900">{selectedForm.name}</div>
                    </div>
                    <div>
                      <label className="text-sm text-slate-500">公司</label>
                      <div className="font-medium text-slate-900">{selectedForm.company}</div>
                    </div>
                    <div>
                      <label className="text-sm text-slate-500">电话</label>
                      <a href={`tel:${selectedForm.phone}`} className="font-medium text-ocean-600 hover:text-ocean-700">
                        {selectedForm.phone}
                      </a>
                    </div>
                    <div>
                      <label className="text-sm text-slate-500">邮箱</label>
                      <div className="font-medium text-slate-900">{selectedForm.email || '-'}</div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-500 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        所属行业
                      </label>
                      <div className="mt-1 font-medium text-slate-900">{selectedForm.industry || '-'}</div>
                    </div>
                    <div>
                      <label className="text-sm text-slate-500 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        关注产品
                      </label>
                      <div className="mt-1 font-medium text-slate-900">{selectedForm.product || '-'}</div>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-slate-500 flex items-center gap-2">
                      <Network className="w-4 h-4" />
                      IP地址
                    </label>
                    <div className="mt-1 font-medium text-slate-900 font-mono">{selectedForm.ip_address || '-'}</div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-slate-500 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      补充说明
                    </label>
                    <div className="mt-2 p-4 bg-slate-50 rounded-lg text-slate-700">
                      {selectedForm.message || '无'}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-slate-500">备注</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="w-full mt-2 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none resize-none"
                      placeholder="添加备注..."
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setSelectedForm(null)}
                      className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      取消
                    </button>
                    <button
                      onClick={() => updateNotes(selectedForm.id)}
                      className="px-4 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition-colors"
                    >
                      保存备注
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
